import styled from 'styled-components';

import { createGradientOrSolid } from 'utils';
import { Gradient } from 'utils/types';
import { breakpoints } from 'styles/breakpoints';
import { SubRollingOfferUiProps } from '../../../../shared/types';
import { motion } from 'framer-motion';
import { Box } from '@mui/material';

export interface StyledSubRollingOfferProps extends SubRollingOfferUiProps {
  clickable?: boolean;
}

interface SubOffersLayoutProps {
  direction?: string;
  justifyContent?: string;
}

interface SubRollingOfferPriceButtonProps {
  background?: Gradient;
  borderRadius?: string;
  isHorizontalViewEnabled: boolean;
}

export const StyledSubRollingOfferPrice = styled.div<SubOffersLayoutProps>`
  display: flex;
  flex-direction: column;
  direction: ltr;
  justify-content: center;
  width: 100%;
  color: var(--button-text-color);
  text-shadow: 0px 2px 2px rgba(0, 0, 0, 0.25);

  .totalPrice {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
  }
`;

export const StyledSubProductsContainer = styled.div<{
  isHorizontalViewEnabled: boolean;
}>`
  display: flex;
  width: 100%;
  height: 100%;
  padding: 4px;
  gap: ${({ isHorizontalViewEnabled }) => (isHorizontalViewEnabled ? 10 : 5)}px;
  flex-direction: column;
  box-sizing: border-box;
  align-items: flex-start;
  justify-content: center;
`;

export const StyledSubRollingOfferPriceButton = styled.div<SubRollingOfferPriceButtonProps>`
  height: 30px;
  min-height: ${({ isHorizontalViewEnabled }) =>
    isHorizontalViewEnabled ? 40 : 30}px;
  width: 100%;
  background: ${({ background }) =>
    background && createGradientOrSolid(background)};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0 0 8px 8px;
  font-weight: 700;
  font-size: 14px;
  color: var(--button-text-color);
  position: relative;

  .white_stripe {
    position: absolute;
    top: 2px;
    height: 8px;
    width: 100%;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0)
    );
  }

  .lockImage {
    position: absolute;
    left: 10px;
    transform-origin: center;
  }
`;

export const StyledMainProduct = styled(motion.div)<{
  title: string;
  color: string;
  isHorizontalViewEnabled: boolean;
}>`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 4px;
  padding: 4px;
  box-sizing: border-box;

  .main-product-image {
    width: ${({ isHorizontalViewEnabled }) =>
      isHorizontalViewEnabled ? 74 : 64}px;
    text-align: center;
    height: ${({ isHorizontalViewEnabled }) =>
      isHorizontalViewEnabled ? 74 : 64}px;
    z-index: 100;
    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }

  .product-image-container {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;

    .badge {
      box-sizing: content-box;
      height: 26px;
      max-width: 24px;
      margin-right: -30px;
      padding-left: 12px;
      padding-right: 31px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      top: 9px;
      z-index: 1;
      transform: translateY(-50%);
      background-image: url('https://media.appcharge.com/defaults/product-badge-huge.png');
      background-size: 100% 100%;

      .badge-text {
        position: relative;
        font-size: 12px;
        padding-left: var(--padding-left);
        font-weight: 600;
        margin: auto 0;
        background: -webkit-linear-gradient(
          rgb(255, 247, 199),
          rgb(226, 182, 109)
        );
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;

        &:after {
          background: none;
          content: '${(props: any) => CSS.escape(props.badgeText || '')}';
          padding-left: var(--padding-left);
          left: 0;
          top: 0;
          z-index: -1;
          position: absolute;
          text-shadow: 0 1px 0px #000000;
        }
      }
    }

    .tooltip {
      height: 16px;
      width: 16px;
      position: absolute;
      top: 9px;
      right: ${({ isHorizontalViewEnabled }) =>
        isHorizontalViewEnabled ? 65 : 55}px;
      transform: translateY(-100%);
      z-index: 200;
    }
  }

  .text {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: center !important;
    position: relative;
    z-index: 1;
    padding-top: 0;
    gap: 4px;

    .tooltip {
      font-size: 10px;
      font-weight: 400;
      z-index: 4;
      margin: 0;
      color: ${(props) => props.color};
    }

    .quantity {
      margin: 0;
      text-align: center;
      font-weight: 600;
      font-size: 14px;
      color: ${({ color }) => color};
      position: relative;
      direction: ltr;

      &:after {
        background: none;
        content: '${({ title }) => CSS.escape(title || '')}';
        left: 0;
        top: 0;
        z-index: -1;
        position: absolute;
        direction: ltr;
      }
    }
    .stroked {
      font-weight: 600;
      color: white;
      position: relative;
      margin: 0;
      text-shadow: 0 2px black;

      &::after {
        content: ' ';
        height: 2px;
        width: 100%;
        background: #d8232a;
        position: absolute;
        left: 0;
        top: 50%;
      }
    }
  }

  @media (max-width: ${breakpoints.mobileM}) {
    img {
      &.mainPic {
        width: 70px;
      }
    }
  }

  @media (max-width: ${breakpoints.mobileS}) {
    img.mainPic {
      width: 64px !important;
    }
  }
`;

export const StyledSubProduct = styled(motion.div)<{
  isHorizontalViewEnabled: boolean;
  hasBadge: boolean;
}>`
  width: 100%;
  height: ${({ isHorizontalViewEnabled }) =>
    isHorizontalViewEnabled ? 32 : 28}px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  justify-content: start;

  .product-image-container {
    position: relative;
    padding-left: 4px;
    display: flex;
    flex-direction: row;
    align-items: center;

    .product {
      position: relative;
      z-index: 2;
      height: 38px;
    }

    .tooltip {
      height: 12px;
      width: 12px;
      position: absolute;
      top: 5px;
      left: ${({ hasBadge }) => (hasBadge ? 30 : 0)}px;
      transform: translateY(-100%);
      z-index: 200;
    }

    .badge {
      box-sizing: content-box;
      height: 18px;
      max-width: 24px;
      margin-right: -10px;
      padding-left: 7px;
      padding-right: 11px;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      top: 9px;
      z-index: 1;
      transform: translateY(-50%);
      background-image: url('https://media.appcharge.com/defaults/product-badge-huge.png');
      background-size: 100% 100%;

      .badge-text {
        position: relative;
        font-size: 12px;
        padding-left: var(--padding-left);
        font-weight: 600;
        margin: auto 0;
        background: -webkit-linear-gradient(
          rgb(255, 247, 199),
          rgb(226, 182, 109)
        );
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;

        &:after {
          background: none;
          content: '${(props: any) => CSS.escape(props.badgeText || '')}';
          padding-left: var(--padding-left);
          left: 0;
          top: 0;
          z-index: -1;
          position: absolute;
          text-shadow: 0 1px 0px #000000;
        }
      }
    }
  }

  .sub-product-image {
    width: ${({ isHorizontalViewEnabled }) =>
      isHorizontalViewEnabled ? 32 : 28}px;
    height: ${({ isHorizontalViewEnabled }) =>
      isHorizontalViewEnabled ? 32 : 28}px;
    z-index: 100;
    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }

  .text {
    max-width: ${({ hasBadge }) =>
      hasBadge ? 'calc(100% - 70px)' : 'calc(100% - 40px)'};
    padding: 0;
    flex-direction: column;
    gap: 4px;

    p {
      font-size: 14px;
      font-weight: 600;
      color: ${(props) => props.color};
      margin: 0;
    }

    .tooltip {
      font-size: 10px;
      font-weight: 400;
      z-index: 4;
      margin: 0;
    }
  }
`;
